import { normaliseCustomerDisplayName, stripLeadingCustomerSalutation } from "./customerName";

export function normalizeSearchText(raw: string | null | undefined): string {
  const value = String(raw ?? "")
    .normalize("NFKC")
    .toLowerCase()
    .replace(/[\u2018\u2019\u201c\u201d]/g, "'");
  const withoutSalutation = stripLeadingCustomerSalutation(value);
  return withoutSalutation
    .replace(/[^\p{L}\p{N}\s]/gu, " ")
    .replace(/\s+/g, " ")
    .trim();
}

export function compactSearchName(raw: string | null | undefined): string {
  return normalizeSearchText(raw).replace(/\s+/g, "");
}

export function searchNameTokens(raw: string | null | undefined): string[] {
  const text = normalizeSearchText(raw);
  return text ? text.split(" ").filter(Boolean) : [];
}

/** Initials of given names + full last token, compacted (Tiak Koon Loh → tkloh). */
export function compactInitialSurnameForm(raw: string | null | undefined): string | null {
  const tokens = searchNameTokens(raw);
  if (tokens.length < 2) return tokens[0] ?? null;
  const last = tokens[tokens.length - 1]!;
  const given = tokens.slice(0, -1).map((t) => t[0]).join("");
  return `${given}${last}`;
}

export function initialsOnly(raw: string | null | undefined): string | null {
  const tokens = searchNameTokens(raw);
  if (tokens.length === 0) return null;
  return tokens.map((t) => t[0]).join("");
}

export type NameMatchKind =
  | "exact_name"
  | "exact_alias"
  | "initials"
  | "token_prefix"
  | "partial"
  | null;

export function classifyNameMatch(query: string, candidateName: string, matchedAlias?: string | null): NameMatchKind {
  const qNorm = normalizeSearchText(query);
  const cNorm = normalizeSearchText(candidateName);
  const aliasNorm = matchedAlias ? normalizeSearchText(matchedAlias) : "";
  if (!qNorm) return null;
  if (aliasNorm && aliasNorm === qNorm) return "exact_alias";
  if (cNorm === qNorm) return "exact_name";

  const qCompact = compactSearchName(query);
  const cCompact = compactSearchName(candidateName);
  const cInitials = compactInitialSurnameForm(candidateName);
  const qInitials = compactInitialSurnameForm(query);
  if (
    qCompact &&
    (qCompact === cCompact ||
      qCompact === cInitials ||
      qInitials === cCompact ||
      qInitials === cInitials ||
      qCompact === initialsOnly(candidateName))
  ) {
    return "initials";
  }

  const qTokens = searchNameTokens(query);
  const cTokens = searchNameTokens(candidateName);
  if (qTokens.length > 0 && cTokens.length > 0) {
    const prefixHits = qTokens.every((qt) =>
      cTokens.some((ct) => ct.startsWith(qt) || qt.startsWith(ct)),
    );
    if (prefixHits && (qTokens.length >= 2 || qTokens[0]!.length >= 3)) {
      return "token_prefix";
    }
  }

  if (cNorm.includes(qNorm) || qNorm.includes(cNorm) || cCompact.includes(qCompact) || qCompact.includes(cCompact)) {
    return "partial";
  }
  return null;
}

export function formatCustomerSearchAddress(input: {
  address?: string | null;
  unitNumber?: string | null;
  postalCode?: string | null;
}): string {
  const address = (input.address ?? "").trim();
  const unit = (input.unitNumber ?? "").trim();
  const postal = (input.postalCode ?? "").trim();
  const parts: string[] = [];
  if (address) parts.push(address);
  if (unit) parts.push(unit);
  if (postal) parts.push(postal.length === 6 && /^\d+$/.test(postal) ? `Singapore ${postal}` : postal);
  return parts.join(", ");
}

export { normaliseCustomerDisplayName };
